<template>
  <div class="certification-page" v-slideIn>
    <div class="page-head-container">
      <PageHead pageName="资质认证" />
    </div>
    <Steps :current="3" />
    <div class="page-wrapper" v-loading.lock="loading">
      <div v-if="loading" class="page-empty"></div>
      <div v-else>
        <div class="form-area">
          <status-info
            :certification="certification"
            v-if="certification.auditStatus"
          />
          <div class="form-block">
            <h3 class="head">基础信息</h3>
            <div class="form-item-container"></div>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span
                >公司名称:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  maxlength="50"
                  type="text"
                  v-model="enterpriseCertAddVO.companyNameZh"
                  :error="enterpriseCertAddVOErrors.companyNameZh"
                  :onBlur="handleEnterpriseCertValidate('companyNameZh')"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.companyNameZh }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">公司简称:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  maxlength="100"
                  type="text"
                  v-model="enterpriseCertAddVO.companyAbbreviation"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.companyAbbreviation }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">公司曾用名:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  maxlength="100"
                  type="text"
                  v-model="enterpriseCertAddVO.usedCompanyName"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.usedCompanyName }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">公司英文名:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  maxlength="100"
                  type="text"
                  v-model="enterpriseCertAddVO.companyNameEn"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.companyNameEn }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span
                >所在地:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <select
                  v-model="enterpriseCertAddVO.provinceCode"
                  class="form-item-select"
                  @change="handleProvinceChange"
                >
                  <option
                    v-for="province in provinceList"
                    :key="province.id"
                    :value="province.id"
                  >
                    {{ province.name }}
                  </option>
                </select>
                <select
                  v-model="enterpriseCertAddVO.cityCode"
                  class="form-item-select"
                  @change="handleCityChange"
                >
                  <option
                    v-for="city in cityList"
                    :key="city.id"
                    :value="city.id"
                  >
                    {{ city.name }}
                  </option>
                </select>
                <select
                  v-model="enterpriseCertAddVO.districtCode"
                  class="form-item-select"
                  @change="handleDistrictChange"
                >
                  <option
                    v-for="district in districtList"
                    :key="district.id"
                    :value="district.id"
                  >
                    {{ district.name }}
                  </option>
                </select>
                <div
                  style="color: #e32422; font-size: 12px"
                  class="district-error-tip"
                >
                  {{ enterpriseCertAddVOErrors.district }}
                </div>
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.location }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span
                >详细地址:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  type="text"
                  v-model="enterpriseCertAddVO.address"
                  :error="enterpriseCertAddVOErrors.address"
                  :onBlur="handleEnterpriseCertValidate('address')"
                  maxlength="60"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.address }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span
                >公司注册时间:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <el-date-picker
                  style="width: 300px"
                  type="date"
                  placeholder="选择日期"
                  v-model="enterpriseCertAddVO.registerTime"
                  :picker-options="{ disabledDate: disabledRegisterTimeRange }"
                  @change="handleRegisterTimeChange"
                />
                <p
                  class="form-item-inner--error"
                  v-if="enterpriseCertAddVOErrors.registerTime"
                >
                  {{ enterpriseCertAddVOErrors.registerTime }}
                </p>
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.registerTime | formatDate }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">企业网站:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  type="text"
                  v-model="enterpriseCertAddVO.website"
                  maxlength="120"
                  :error="enterpriseCertAddVOErrors.website"
                  :onBlur="handleEnterpriseCertValidate('website')"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.website }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span
                >受邀钢厂:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <select
                  class="form-item-select form-item-select--large"
                  v-model="enterpriseCertAddVO.invitedSteel"
                  @change="handleInvitedSteelChange"
                >
                  <option value="###">请选择受邀钢厂</option>
                  <option
                    v-for="factory in factoryList"
                    :key="factory.code || ''"
                    :value="factory.code || ''"
                  >
                    {{ factory.name }}
                  </option>
                </select>
                <span
                  v-if="enterpriseCertAddVOErrors.invitedSteel"
                  class="form-item-inner--error"
                  >{{ enterpriseCertAddVOErrors.invitedSteel }}</span
                >
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.invitedSteel || '自己感兴趣，无受邀钢厂' }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">认证目的:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  type="text"
                  v-model="enterpriseCertAddVO.authPurpose"
                  maxlength="50"
                  placeholder="比如参与交易、招投标等"
                  :primary="true"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.authPurpose }}
              </div>
            </div>
          </div>
          <div class="form-block">
            <h3 class="head">联系方式</h3>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span>联系人:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  type="text"
                  maxlength="10"
                  v-model="enterpriseCertAddVO.contactPerson"
                  :error="enterpriseCertAddVOErrors.contactPerson"
                  :onBlur="handleEnterpriseCertValidate('contactPerson')"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.contactPerson }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span class="required-symbol" v-if="hasRejectCert">*</span
                >联系电话:
              </span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  inline
                  number
                  type="text"
                  maxlength="11"
                  v-model="enterpriseCertAddVO.contactPhone"
                  :error="enterpriseCertAddVOErrors.contactPhone"
                  :onBlur="handleEnterpriseCertValidate('contactPhone')"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.contactPhone }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">企业电话:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  type="text"
                  maxlength="15"
                  v-model="enterpriseCertAddVO.enterprisePhone"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.enterprisePhone }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">企业传真:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  :inline="true"
                  type="text"
                  maxlength="20"
                  :error="enterpriseCertAddVOErrors.faxError"
                  :onBlur="handleEnterpriseCertValidate('faxError')"
                  v-model="enterpriseCertAddVO.corporateFax"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.corporateFax }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">邮编:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  inline
                  number
                  type="text"
                  maxlength="6"
                  v-model="enterpriseCertAddVO.zipCode"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.zipCode }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">QQ:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <Input
                  inline
                  number
                  type="text"
                  maxlength="12"
                  v-model="enterpriseCertAddVO.qq"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.qq }}
              </div>
            </div>
          </div>
          <div class="form-block">
            <h3 class="head">证件附件</h3>
            <div class="form-item__images">
              <template v-if="hasRejectCert">
                <span class="upload-container">
                  <Upload
                    :url="enterpriseCertAddVO.bizLicensePreview"
                    :onRemove="onBizLicenseRemove"
                    :onSuccess="onBizLicenseSuccess"
                    :action="uploadApi"
                    :error="enterpriseCertAddVOErrors.bizLicense"
                    :beforeUpload="beforeUpload"
                  />
                  <div class="bottom">
                    <span class="required">*</span>
                    <span class="label">营业执照</span>
                  </div>
                </span>
                <span class="upload-container">
                  <Upload
                    :url="enterpriseCertAddVO.authorizationPreview"
                    :onRemove="onAuthorizationRemove"
                    :onSuccess="onAuthorizationSuccess"
                    :action="uploadApi"
                    :error="enterpriseCertAddVOErrors.authorization"
                    :beforeUpload="beforeUpload"
                  />
                  <div class="bottom">
                    <span class="required-symbol">*</span>
                    <span class="label">委托书</span>
                    <div>
                      <a
                        :href="authorizationFilesPath"
                        class="download-template-btn"
                        >下载模板</a
                      >
                    </div>
                  </div>
                </span>
                <span class="upload-container">
                  <Upload
                    :url="enterpriseCertAddVO.orgCodePreview"
                    :onRemove="onOrgCodeRemove"
                    :onSuccess="onOrgCodeSuccess"
                    :action="uploadApi"
                    :beforeUpload="beforeUpload"
                  />
                  <div class="bottom">
                    <span class="label">组织机构代码</span>
                  </div>
                </span>
                <span class="upload-container">
                  <Upload
                    :url="enterpriseCertAddVO.taxRegCertPrevew"
                    :onRemove="onTaxRegCertRemove"
                    :onSuccess="onTaxRegCertSuccess"
                    :action="uploadApi"
                    :beforeUpload="beforeUpload"
                  />
                  <div class="bottom">
                    <span class="label">税务登记证</span>
                  </div>
                </span>
              </template>
              <div class="form-item" v-else>
                <div class="img">
                  <img
                    v-if="certification.bizLicense"
                    :src="certification.bizLicense"
                  />
                  <p class="file-label">营业执照</p>
                  <p v-if="!certification.bizLicense" class="placeholder">
                    暂未上传
                  </p>
                </div>
                <div class="img">
                  <img
                    v-if="certification.authorization"
                    :src="certification.authorization"
                  />
                  <p class="file-label">委托书</p>
                  <p v-if="!certification.authorization" class="placeholder">
                    暂未上传
                  </p>
                </div>
                <div class="img">
                  <img
                    v-if="certification.orgCode"
                    :src="certification.orgCode"
                  />
                  <p class="file-label">组织机构代码</p>
                  <p v-if="!certification.orgCode" class="placeholder">
                    暂未上传
                  </p>
                </div>
                <div class="img">
                  <img
                    v-if="certification.taxRegCert"
                    :src="certification.taxRegCert"
                  />
                  <p class="file-label">税务登记证</p>
                  <p v-if="!certification.taxRegCert" class="placeholder">
                    暂未上传
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="form-block" v-if="hasCenpur">
            <h3 class="head">开户信息</h3>
            <div class="form-item">
              <span class="form-item-label">
                <span
                  class="required-symbol"
                  v-if="
                    !hasOpenningOrOpenedCenpur || !certification.accountLicense
                  "
                  >*</span
                >开户许可证:
              </span>
              <div
                class="form-item-inner form-item-inner--mutiiple-line"
                v-if="
                  !hasOpenningOrOpenedCenpur || !certification.accountLicense
                "
              >
                <Upload
                  :url="enterpriseCertAddVO.accountLicensePreview"
                  :onRemove="onAccountLicenseRemove"
                  :onSuccess="onAccountLicenseSuccess"
                  :action="uploadApi"
                  :error="enterpriseCertAddVOErrors.accountLicense"
                  :beforeUpload="beforeUpload"
                />
              </div>
              <div class="form-item-inner form-item-inner--imgs" v-else>
                <div class="form-item-inner--imgs-item">
                  <img :src="certification.accountLicense" />
                </div>
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span
                  class="required-symbol"
                  v-if="!hasOpenningOrOpenedCenpur || !certification.openBank"
                  >*</span
                >开户行:
              </span>
              <div
                class="form-item-inner"
                v-if="!hasOpenningOrOpenedCenpur || !certification.openBank"
              >
                <Input
                  type="text"
                  maxlength="50"
                  v-model="enterpriseCertAddVO.openBank"
                  :inline="true"
                  :error="enterpriseCertAddVOErrors.openBank"
                  :onBlur="handleEnterpriseCertValidate('openBank')"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.openBank }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span
                  class="required-symbol"
                  v-if="
                    !hasOpenningOrOpenedCenpur || !certification.accountName
                  "
                  >*</span
                >户名:
              </span>
              <div
                class="form-item-inner"
                v-if="!hasOpenningOrOpenedCenpur || !certification.accountName"
              >
                <Input
                  type="text"
                  maxlength="50"
                  v-model="enterpriseCertAddVO.accountName"
                  :inline="true"
                  :error="enterpriseCertAddVOErrors.accountName"
                  :onBlur="handleEnterpriseCertValidate('accountName')"
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.accountName }}
              </div>
            </div>
            <div class="form-item">
              <span class="form-item-label">
                <span
                  class="required-symbol"
                  v-if="!hasOpenningOrOpenedCenpur || !certification.accountNo"
                  >*</span
                >帐号:
              </span>
              <div
                class="form-item-inner"
                v-if="!hasOpenningOrOpenedCenpur || !certification.accountNo"
              >
                <Input
                  type="text"
                  maxlength="50"
                  v-model="enterpriseCertAddVO.accountNo"
                  :inline="true"
                  :error="enterpriseCertAddVOErrors.accountNo"
                  :onBlur="handleEnterpriseCertValidate('accountNo')"
                  number
                />
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.accountNo }}
              </div>
            </div>
          </div>
          <!-- additionalCert -->
          <div class="form-block" v-if="hasSupplier">
            <category-select
              @onChange="handleSupplierChange"
              :disabled="hasOpenningOrOpenedSupplier"
              :tree-data="categoryTree"
              :defaultCheckedKeys="additionalCertVO.supplyCategory"
            >
              <h3 class="head">供应品类</h3>
              <div class="form-item form-item--space">
                <span class="form-item-label">
                  <span
                    class="required-symbol"
                    v-if="!hasOpenningOrOpenedSupplier"
                    >*</span
                  >厂家类型:
                </span>
                <div
                  class="form-item-inner form-item-inner--text"
                  v-if="hasOpenningOrOpenedSupplier"
                >
                  {{ additionalCertVO.manufactorTypeName }}
                </div>
                <div class="form-item-inner" v-else>
                  <select
                    class="form-item-select form-item-select--large"
                    v-model="additionalCertVO.manufactorType"
                    @change="handleManufactorChange"
                  >
                    <option value="" disabled>请选择</option>
                    <option
                      :value="el.code"
                      :key="el.code"
                      v-for="el in manufactorOptions"
                    >
                      {{ el.name }}
                    </option>
                  </select>
                  <span
                    class="form-item-inner--error"
                    v-if="additionalCertVOErrors.manufactorType"
                  >
                    {{ additionalCertVOErrors.manufactorType }}
                  </span>
                </div>
              </div>
            </category-select>
          </div>
          <div class="form-block" v-if="hasPurchaser">
            <category-select
              moduleType="采购"
              @onChange="handlePurchaserChange"
              :disabled="hasOpenningOrOpenedPurchaser"
              :tree-data="categoryTree"
              :defaultCheckedKeys="additionalCertVO.purchaserCategory"
            >
              <h3 class="head">采购品类</h3>
            </category-select>
          </div>
          <div class="form-block" v-if="hasCenpur">
            <h3 class="head">外贸信息</h3>
            <div class="form-item">
              <span class="form-item-label">外贸资质:</span>
              <div class="form-item-inner" v-if="hasRejectCert">
                <radio-group v-model="additionalCertVO.foreignTradeCert">
                  <radio-item
                    v-for="c in certificationOptions"
                    :key="c.value"
                    :label="c.label"
                    :value="c.value"
                  />
                </radio-group>
              </div>
              <div class="form-item-inner form-item-inner--text" v-else>
                {{ certification.foreignTradeCert ? '具备' : '不具备' }}
              </div>
            </div>
            <template v-if="additionalCertVO.foreignTradeCert">
              <div class="form-item">
                <span class="form-item-label"
                  >对外贸易经营者备案登记表附件:</span
                >
                <div
                  class="form-item-inner form-item-inner--mutiiple-line"
                  v-if="hasRejectCert"
                >
                  <Upload
                    :url="additionalCertVO.foreignTradeRegistryPreview"
                    :onRemove="handleForeignTradeRegistryRemove"
                    :onSuccess="handleForeignTradeRegistryChange"
                    :action="uploadApi"
                    :beforeUpload="beforeUpload"
                    :error="additionalCertVOErrors.foreignTradeRegistry"
                  />
                </div>
                <div class="form-item-inner form-item-inner--imgs" v-else>
                  <div class="form-item-inner--imgs-item">
                    <img :src="certification.foreignTradeRegistry" />
                  </div>
                </div>
              </div>
              <div class="form-item">
                <span class="form-item-label">进出口企业代码号:</span>
                <div class="form-item-inner" v-if="hasRejectCert">
                  <Input
                    :inline="true"
                    type="text"
                    maxlength="20"
                    :error="additionalCertVOErrors.foreignTradeCode"
                    :onBlur="handleAdditionalCertValidate('foreignTradeCode')"
                    v-model="additionalCertVO.foreignTradeCode"
                  />
                </div>
                <div class="form-item-inner form-item-inner--text" v-else>
                  {{ certification.foreignTradeCode }}
                </div>
              </div>
            </template>
          </div>
          <!-- <div class="audit-status-container" v-if="certification.auditStatus === certificationStatusCode.reject">
            <img :src="auditRefuseImg" alt="审核拒绝" />
            <span style="color: red;">认证失败</span>
          </div>-->
        </div>
        <div class="form-buttons">
          <span class="btn-container">
            <Button type="hollow-red" text="上一步" :onClick="goBack" />
          </span>
          <span class="btn-container">
            <Button
              text="确定提交"
              type="danger"
              :onClick="handleSubmit"
              :disabled="submitting"
              :loading="submitting"
            />
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import uniq from 'lodash/uniq';
  import Steps from '@/components/Steps/index.vue';
  import PageHead from '@/components/PageHead/index.vue';
  import Input from '@/components/Input/index.vue';
  import Button from '@/components/Button/index.vue';
  import Message from '@/components/Message';
  import Upload from '@/components/Upload/index.vue';
  import CategorySelect from '@/modules/CategorySelect';
  import RadioGroup from '@/components/Checkbox/RadioGroup';
  import RadioItem from '@/components/Checkbox/RadioGroup/item';
  import StatusInfo from '../status';

  import api from '@/api';
  import env from '@/utils/env';
  import { mixins, formatStringSymbol, validIsSupplier, validIsPurchaser } from '@/utils';
  import {
    routes,
    platformCode,
    indentityCode,
    enterpriseCertRules,
    additionalCertRules,
    certificationStatusCode,
    openningAndOpenedStatus,
    bankInfoRules,
    authorizationFilesPath,
  } from '@/config';

  const purchaserCode = indentityCode.cenpur.purchaser;
  const supplierCode = indentityCode.cenpur.supplier;
  const bidPurchase = indentityCode.bid.purchaser;
  const bidSupplierCode = indentityCode.bid.supplier;

  const cenpurCode = platformCode.cenpur;
  const bidCode = platformCode.bid;

  const cenpurIdentityCodes = [purchaserCode, supplierCode];

  export default {
    name: 'update-company-certification',
    data() {
      return {
        submitting: false,
        auditRefuseImg: '',
        certificationStatusCode,
        authorizationFilesPath,
        certificationOptions: [
          {
            label: '具备',
            value: true,
          },
          {
            label: '不具备',
            value: false,
          },
        ],
        enterpriseCertAddVO: {
          companyNameZh: '',
          companyNameEn: '',
          companyAbbreviation: '',
          usedCompanyName: '',
          provinceCode: '',
          cityCode: '',
          districtCode: '',
          address: '',
          registerTime: '',
          website: '',
          authPurpose: '',
          contactPerson: '',
          contactPhone: '',
          enterprisePhone: '',
          zipCode: '',
          corporateFax: '',
          // 受邀钢厂
          invitedSteel: '###',

          bizLicense: '',
          bizLicensePreview: '',

          qq: '',
          authorization: '',
          taxRegCert: null,
          foreignTradeCert: false,

          accountLicense: null,
          accountLicensePreview: '',
          openBank: '',
          accountName: '',
          accountNo: '',
        },
        enterpriseCertAddVOErrors: {
          companyNameZh: '',
          companyNameEn: '',
          companyAbbreviation: '',
          usedCompanyName: '',
          provinceCode: '',
          cityCode: '',
          districtCode: '',
          address: '',
          registerTime: '',
          website: '',
          authPurpose: '',
          contactPerson: '',
          contactPhone: '',
          enterprisePhone: '',
          zipCode: '',
          corporateFax: '',
          // 受邀钢厂
          invitedSteel: '',

          bizLicense: '',

          qq: '',
          authorization: '',
          taxRegCert: '',
          foreignTradeCert: '',

          accountLicense: '',
          openBank: '',
          accountName: '',
          accountNo: '',
        },

        additionalCertVO: {
          foreignTradeCert: false,
          foreignTradeCode: '',
          foreignTradeRegistry: '',
          manufactorType: '',
          supplyCategory: [],
          purchaserCategory: [],
        },
        additionalCertVOErrors: {
          foreignTradeCert: '',
          foreignTradeCode: '',
          manufactorType: '',
          /**
           * NOTE
           * 由于上传差价使用的是本地预览，这里拿到的URL是没法直接预览的，只是用来提交到后台的URL，
           * 所以这里不对上传插件返回的URL映射到上传插件的url属性当中
           */
          foreignTradeRegistry: '',
          foreignTradeRegistryPreview: '',

          supplyCategory: '',
          purchaserCategory: '',
        },

        token: localStorage.getItem('sso_biz_token'),
        loading: false,
        certification: {}, // 已提交的资质信息
        uploadApi: api.getUploadApi(),

        provinceList: [],
        cityList: [],
        districtList: [],
        cityDict: [],
        districtDict: [],

        factoryList: [],
        categoryTree: [],
        uploadingFileCounts: 0, // 正在上传的文件数量

        openedPlatformList: [],
        manufactorOptions: [],

        certStatus: {
          hasCert: false,
          isAuditPass: false,
          auditStatus: '',
          hasRejectCert: false,
          isEnterpriseCert: false,
          isIndividualCert: false,
          hasAdditionalCert: false,
          hasFundsAccount: false,
          identities: [],
        },
      };
    },
    mixins: [mixins],
    created() {
      setTimeout(() => {
        let baseTitle = JSON.parse(localStorage.getItem('WEB_INFO')).title || '';
        document.title = `${baseTitle} - 资质认证`;
      }, 500);
    },
    methods: {
      disabledRegisterTimeRange(time) {
        return time.getTime() > Date.now();
      },
      handleEnterpriseCertChange(name) {
        return this.setFieldValue(this.enterpriseCertAddVO, name);
      },
      handleEnterpriseCertValidate(name) {
        return this.validateFieldWithErrors(
          this.enterpriseCertAddVOErrors,
          enterpriseCertRules,
          name
        );
      },
      handleAdditionalCertChange(name) {
        return this.setFieldValue(this.additionalCertVO, name);
      },
      handleAdditionalCertValidate(name) {
        return this.validateFieldWithErrors(
          this.additionalCertVOErrors,
          additionalCertRules,
          name
        );
      },
      handleRegisterTimeChange(value) {
        this.enterpriseCertAddVO.registerTime = +value;
        if (!value) {
          this.enterpriseCertAddVOErrors.registerTime = '请选择公司注册时间';
        } else {
          this.enterpriseCertAddVOErrors.registerTime = '';
        }
      },
      handleInvitedSteelChange(e) {
        if (e.target.value === '###') {
          this.enterpriseCertAddVOErrors.invitedSteel = '请选择受邀钢厂';
        } else {
          this.enterpriseCertAddVOErrors.invitedSteel = '';
        }
      },
      handleManufactorChange(e) {
        const validateResult = this.validateField(
          e.target.value,
          additionalCertRules.manufactorType
        );
        this.additionalCertVOErrors.manufactorType = validateResult.error;
      },
      handleSupplierChange(data) {
        this.additionalCertVO.supplyCategory = this.resolveCategoryIds(data);
      },
      handlePurchaserChange(data) {
        this.additionalCertVO.purchaserCategory = this.resolveCategoryIds(data);
      },
      handleFactoryChange(e) {
        this.invitedSteel = e.target.value;
        if (e.target.value !== '') {
          this.invitedSteelError = '';
        }
      },
      handleProvinceChange(e) {
        let provinceCode = e.target.value;
        this.enterpriseCertAddVO.provinceCode = provinceCode;
        this.updateCityList(provinceCode, true);
      },
      updateCityList(provinceCode, isInitSelectValue = false) {
        this.cityList = this.cityDict.filter(
          city => city.parentId === provinceCode
        );
        if (isInitSelectValue && this.cityList[0]) {
          this.enterpriseCertAddVO.cityCode = this.cityList[0].id;
          this.updateDistrictList(this.enterpriseCertAddVO.cityCode, true);
        }
      },
      handleCityChange(e) {
        let cityCode = e.target.value;
        this.enterpriseCertAddVO.cityCode = cityCode;
        this.updateDistrictList(cityCode, true);
      },
      updateDistrictList(cityCode, isInitSelectValue = false) {
        this.districtList = this.districtDict.filter(
          district => district.parentId === cityCode
        );
        if (isInitSelectValue && this.districtList[0]) {
          this.enterpriseCertAddVO.districtCode = this.districtList[0].id;
        }
      },
      handleDistrictChange(e) {
        let v = e.target.value;
        this.enterpriseCertAddVO.districtCode = v;
      },
      onBizLicenseSuccess(res) {
        this.uploadingFileCounts--;
        if (res.code === 0) {
          this.enterpriseCertAddVO.bizLicense = res.data.url;
          this.enterpriseCertAddVOErrors.bizLicense = '';
        }
      },
      onBizLicenseRemove() {
        this.enterpriseCertAddVO.bizLicense = '';
        this.enterpriseCertAddVOErrors.bizLicense = '请上传营业执照';
      },
      onAuthorizationSuccess(res) {
        this.uploadingFileCounts--;
        if (res.code === 0) {
          this.enterpriseCertAddVO.authorization = res.data.url;
          this.enterpriseCertAddVOErrors.authorization = '';
        }
      },
      onAuthorizationRemove(res) {
        this.enterpriseCertAddVO.authorization = '';
      },
      onOrgCodeSuccess(res) {
        this.uploadingFileCounts--;
        if (res.code === 0) {
          this.enterpriseCertAddVO.orgCode = res.data.url;
        }
      },
      onOrgCodeRemove() {
        this.orgCode = '';
      },
      onTaxRegCertSuccess(res) {
        this.uploadingFileCounts--;
        if (res.code === 0) {
          this.enterpriseCertAddVO.taxRegCert = res.data.url;
        }
      },
      onTaxRegCertRemove() {
        this.enterpriseCertAddVO.taxRegCert = '';
      },
      onAccountLicenseSuccess(res) {
        this.uploadingFileCounts--;
        if (res.code === 0) {
          this.enterpriseCertAddVO.accountLicense = res.data.url;
          this.enterpriseCertAddVOErrors.accountLicense = '';
        }
      },
      onAccountLicenseRemove() {
        this.enterpriseCertAddVO.accountLicense = '';
        this.enterpriseCertAddVOErrors.accountLicense = '请上传开户许可证';
      },
      handleForeignTradeRegistryChange(res) {
        if (res.code === 0) {
          this.uploadingFileCounts--;
          this.additionalCertVO.foreignTradeRegistry = res.data.url;
          this.additionalCertVOErrors.foreignTradeRegistry = '';
        }
      },
      handleForeignTradeRegistryRemove() {
        this.additionalCertVO.foreignTradeRegistry = '';
        this.additionalCertVOErrors.foreignTradeRegistry = '';
      },
      beforeUpload(file) {
        if (!/^image\//.test(file.type)) {
          Message.error('只能上传图片文件');
          return false;
        }
        if (file.size > 1024 * 1024 * 10) {
          Message.error('文件大小不能超过10M');
          return false;
        }
        this.uploadingFileCounts++;
        return true;
      },

      validateFileUploadComplete() {
        if (this.uploadingFileCounts !== 0) {
          Message.warning('请等待文件上传完毕再提交');
          return false;
        }
        return true;
      },
      validatePurchaser() {
        if (
          this.hasPurchaser &&
          !this.additionalCertVO.purchaserCategory.length
        ) {
          this.$message.error('请选择采购品类');
          return false;
        }
        return true;
      },
      validateSupplier() {
        // console.log('this.hasSupplier = ', this.hasSupplier);
        // console.log(
        //   'this.additionalCertVO.supplyCategory = ',
        //   this.additionalCertVO.supplyCategory
        // );
        // console.log(
        //   '!this.additionalCertVO.supplyCategory.length = ',
        //   !this.additionalCertVO.supplyCategory.length
        // );
        if (this.hasSupplier && !this.additionalCertVO.supplyCategory.length) {
          this.$message.error('请选择供应品类');
          return false;
        }
        return true;
      },
      validateEnteriseCert() {
        const { isValid, errors } = this.validateFields(
          this.enterpriseCertAddVO,
          enterpriseCertRules
        );
        this.enterpriseCertAddVOErrors = errors;
        return isValid;
      },
      /**
       * @param {Array} preCondition
       */
      validateAdditionalCert(preCondition = []) {
        const shouldValidateValues = { ...this.additionalCertVO };

        preCondition.forEach(([key, condition]) => {
          if (!condition) {
            console.info(
              `%c field: '${key}' was skiped, Because it doesn't need to be checked`,
              'background:#409EFF;color:#fff;opacity:0.75'
            );
            delete shouldValidateValues[key];
          }
        });

        const { isValid, errors } = this.validateFields(
          shouldValidateValues,
          additionalCertRules
        );
        this.additionalCertVOErrors = errors;
        return isValid;
      },
      validateBankInfo(bankInfo = {}) {
        const { isValid, errors } = this.validateFields(bankInfo, bankInfoRules);

        this.enterpriseCertAddVOErrors.openBank = errors.openBank;
        this.enterpriseCertAddVOErrors.accountName = errors.accountName;
        this.enterpriseCertAddVOErrors.accountNo = errors.accountNo;
        this.enterpriseCertAddVOErrors.accountLicense = errors.accountLicense;

        return isValid;
      },
      validate() {
        const validResult = [this.validateFileUploadComplete()];

        if (this.hasRejectCert) {
          validResult.push(this.validateEnteriseCert());
        } else if (this.hasCenpur) {
          const {
            openBank,
            accountName,
            accountNo,
            accountLicense,
          } = this.enterpriseCertAddVO;
          validResult.push(
            this.validateBankInfo({
              openBank,
              accountName,
              accountNo,
              accountLicense,
            })
          );
        }

        if (this.isValidateSupplierFields) {
          validResult.push(this.validateSupplier());
        }

        if (this.isValidatePurchaserFields) {
          validResult.push(this.validatePurchaser());
        }
        if (this.isValidateSupplierFields || this.isValidatePurchaserFields) {
          validResult.push(
            this.validateAdditionalCert([
              ['manufactorType', this.isValidateSupplierFields],
            ])
          );
        }

        return validResult.every(result => result);
      },
      createSubmitData() {
        const {
          openBank,
          accountName,
          accountNo,
          accountLicense,
          manufactorType,
          ...others
        } = this.enterpriseCertAddVO;
        let data = {
          platformList: this.platformCodeAndIdentityCode,
        };

        if (this.hasSelectedCenpur) {
          data.additionalCertVO = this.additionalCertVO;
          data.manufactorType = manufactorType;
        }
        // 如果是驳回页面，则提交全部资料
        if (this.hasRejectCert) {
          const {
            companyNameZh,
            companyAbbreviation,
            usedCompanyName,

            // 不用的字段
            accountLicensePreview,
            bizLicensePreview,
            taxRegCertPrevew,
            authorizationPreview,
            orgCodePreview,
            ...enterpriseCertAddVO
          } = this.enterpriseCertAddVO;

          data.enterpriseCertAddVO = {
            ...enterpriseCertAddVO,
            companyNameZh: formatStringSymbol(companyNameZh),
            usedCompanyName: formatStringSymbol(usedCompanyName),
            companyAbbreviation: formatStringSymbol(companyAbbreviation),
          };
        }
        if (this.hasSelectedCenpur) {
          // 否则其他状态的修改，如果是集采身份，则需要传递bankInfo开户信息
          data.bankInfo = {
            openBank,
            accountName,
            accountNo,
            accountLicenseUrl: accountLicense,
          };
        }

        return data;
      },
      handleSubmit() {
        const isValid = this.validate();
        if (!isValid) {
          return;
        }
        const data = this.createSubmitData();

        this.$confirm('确定立即提交吗？', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(() => {
          this.submitting = true;
          const postFunction = this.hasRejectCert
            ? api.authenticate
            : api.updateUserCert;
          postFunction(data).then(res => {
            this.submitting = false;
            if (res.code === 0) {
              this.$message.success('提交成功');
              this.goPlatformIndex();
            }
          });
        });
      },
      goBack() {
        this.$router.replace(routes.chooseService);
      },
      getSupplyCategoryTree() {
        return api.getSupplyCategoryTree({ platformCode: 'bid' }).then(res => {
          this.categoryTree = res.data;
        });
      },
      checkCertStatus() {
        return api.checkCertStatus().then(data => {
          if (data.code === 0) {
            this.certStatus = data.data;
          }
        });
      },
      initValues() {
        const certification = this.certification;

        this.additionalCertVO = {
          foreignTradeCert: !!certification.foreignTradeCert,
          foreignTradeCode: certification.foreignTradeCode,

          foreignTradeRegistry: certification.foreignTradeRegistryFileKey,
          foreignTradeRegistryPreview: certification.foreignTradeRegistry,

          supplyCategory: certification.supplyCategory || [],
          purchaserCategory: certification.purchaserCategory || [],
          manufactorType: certification.manufactorType || '',
        };

        this.enterpriseCertAddVO = {
          companyNameZh: certification.companyNameZh,
          companyNameEn: certification.companyNameEn,
          companyAbbreviation: certification.companyAbbreviation,
          usedCompanyName: certification.usedCompanyName,
          provinceCode: certification.provinceCode,
          cityCode: certification.cityCode,
          districtCode: certification.districtCode,
          address: certification.address,
          registerTime: certification.registerTime,
          website: certification.website,
          authPurpose: certification.authPurpose,
          contactPerson: certification.contactPerson,
          contactPhone: certification.contactPhone,
          enterprisePhone: certification.enterprisePhone,
          zipCode: certification.zipCode,
          corporateFax: certification.corporateFax,
          // 受邀钢厂
          invitedSteel: certification.invitedSteel,

          qq: certification.qq,

          bizLicense: certification.bizLicenseFileKey,
          bizLicensePreview: certification.bizLicense,

          authorization: certification.authorizationFileKey,
          authorizationPreview: certification.authorization,

          taxRegCert: certification.taxRegCertFileKey,
          taxRegCertPrevew: certification.taxRegCert,

          orgCode: certification.orgCodeFileKey,
          orgCodePreview: certification.orgCode,

          accountLicense: certification.accountLicenseFileKey,
          accountLicensePreview: certification.accountLicense,

          openBank: certification.openBank,
          accountName: certification.accountName,
          accountNo: certification.accountNo,
        };

        this.updateCityList(certification.provinceCode);
        this.updateDistrictList(certification.cityCode);
      },
      async init() {
        this.loading = true;
        await this.getSupplyCategoryTree();
        await this.getDistrictDict();
        await this.getFactoryList();
        await this.getCertificationInfo();
        await this.getServicesInfo();
        await this.checkCertStatus();
        await this.fetchManufactorOptions();

        this.initValues();
        this.enterpriseCertAddVO.hasCenpur = this.hasSelectedCenpur;
        this.loading = false;
      },
      getCertificationInfo() {
        return api.getCertificationInfo().then(res => {
          if (res.code === 0) {
            this.certification = res.data || {};
          }
        });
      },
      getServicesInfo() {
        return api
          .getServiceInfo({
            token: this.token,
          })
          .then(data => {
            if (data && data.code === 0) {
              this.openedPlatformList = data.data;
            }
          });
      },
      getFactoryList() {
        return api.getFactoryList().then(res => {
          if (res.code === 0) {
            this.factoryList = res.data;
          }
        });
      },
      fetchManufactorOptions() {
        return api.getManufactoryTypeOptions().then(res => {
          if (res.code === 0) {
            this.manufactorOptions = res.data;
            return res.data;
          }
          return [];
        });
      },
      getDistrictDict() {
        return api.getDistrictDict().then(res => {
          let data = res.data;
          if (res.code === 0) {
            // 默认选中第一个省份的第一个城市的第一个地区
            this.provinceList = data.province;
            this.cityDict = data.city;
            this.districtDict = data.district;
          }
        });
      },
      goPlatformIndex() {

        this.$router.replace({
          path: routes.platformIndex,
          params: this.$route.params,
          query: {
            ...this.$route.query,
            platformList: this.selectedPlatformList.length
              ? this.selectedPlatformList.join(',')
              : this.openedPlatformList
                  .filter(el => {
                    return el.identity.find(
                      el => el.status === certificationStatusCode.openning
                    );
                  })
                  .map(el => el.platformCode)
                  .join(','),
          },
        });
      },
    },
    computed: {
      selectedPlatformList() {
        const platformList = this.$route.query.platformList;
        return platformList ? platformList.split(',') : [];
      },
      selectedIdentityList() {
        const identityList = this.$route.query.identityList;
        return identityList ? identityList.split(',') : [];
      },
      hasSelectedSupplier() {
        return this.selectedIdentityList.includes(supplierCode) || this.selectedIdentityList.includes(bidSupplierCode);
      },
      hasSelectedPurchaser() {
        return this.selectedIdentityList.includes(purchaserCode) || this.selectedIdentityList.includes(bidPurchase);
      },
      hasSelectedCenpur() {
        return this.hasSelectedSupplier || this.hasSelectedPurchaser;
      },

      canEditIdentityList() {
        const canUpdateOpenedIdentityList = this.openedPlatformList.reduce(
          (prevList, platform) => {
            return prevList.concat(
              platform.identity
                .filter(el => {
                  return !openningAndOpenedStatus.includes(el.status);
                })
                .map(el => el.code)
            );
          },
          []
        );
        return uniq(
          canUpdateOpenedIdentityList.concat(this.selectedIdentityList)
        );
      },

      openedCenpurIdentityList() {
        const cenpurPlatform = this.openedPlatformList.find(
          el => el.platformCode === cenpurCode
        ) || { identity: [] };
        return cenpurPlatform.identity
          .filter(el => openningAndOpenedStatus.includes(el.status))
          .map(el => el.code);
      },

      hasOpenningOrOpenedSupplier() {
        return this.openedCenpurIdentityList.includes(supplierCode) || this.openedCenpurIdentityList.includes(bidSupplierCode);
      },

      hasOpenningOrOpenedPurchaser() {
        return this.openedCenpurIdentityList.includes(purchaserCode) || this.openedCenpurIdentityList.includes(bidPurchase);
      },

      hasOpenningOrOpenedCenpur() {
        return (
          this.hasOpenningOrOpenedSupplier || this.hasOpenningOrOpenedPurchaser
        );
      },

      hasPurchaser() {
        if (!this.hasOpenningOrOpenedPurchaser) {
          return this.hasSelectedPurchaser;
        }
        return (
          this.hasSelectedPurchaser ||
          !!this.openedPlatformList.find(el => {
            return (
              (el.platformCode === cenpurCode || el.platformCode === bidCode) &&
              el.identity.find(el => validIsPurchaser(el.code))
            );
          })
        );
      },

      hasSupplier() {
        if (!this.hasOpenningOrOpenedSupplier) {
          return this.hasSelectedSupplier;
        }
        return (
          this.hasSelectedSupplier ||
          !!this.openedPlatformList.find(el => {
            return (
              (el.platformCode === cenpurCode || el.platformCode === bidCode) &&
              el.identity.find(el => validIsSupplier(el.code))
            );
          })
        );
      },

      hasCenpur() {
        return this.hasPurchaser || this.hasSupplier;
      },
      // 是否应该校验供应商身份相关的字段
      isValidateSupplierFields() {
        return this.hasSupplier && !this.hasOpenningOrOpenedSupplier;
      },
      // 是否应该校验采购商身份相关的字段
      isValidatePurchaserFields() {
        return this.hasPurchaser && !this.hasOpenningOrOpenedPurchaser;
      },

      // 其他平台使用的数据
      // otherPlatformHasOpenedOrOPenning() {
      //   return this.openedPlatformList.some(el => {
      //     return el.platformCode !== cenpurCode && el.identity.some(el => {
      //       return openningAndOpenedStatus.includes(el.status);
      //     });
      //   });
      // },
      hasRejectCert() {
        return this.certification.auditStatus === certificationStatusCode.reject;
      },

      // 通用数据
      platformCodeAndIdentityCode() {
        const platformList = this.selectedPlatformList;
        const identityList = this.selectedIdentityList;

        // 勾选了招标（原招采平台），需要默认带上招采平台（原招标平台）的身份和平台code
        // if (platformList.includes(bidCode) && !platformList.includes(cenpurCode)) {
        //   platformList.push(cenpurCode);
        // };

        // if (identityList.includes(bidSupplierCode) && !identityList.includes(supplierCode)) {
        //   identityList.push(supplierCode);
        // }

        // if (identityList.includes(bidPurchase) && !identityList.includes(purchaserCode)) {
        //   identityList.push(purchaserCode);
        // }

        return platformList
          .map(platformCode => {
            return {
              platformCode,
              identityList: identityList.filter(
                code => code.indexOf(platformCode) !== -1
              ),
            };
          })
          .filter(el => {
            return el.identityList.length;
          });
      },
    },
    mounted() {
      this.init();
    },
    components: {
      Steps,
      Input,
      Button,
      Upload,
      PageHead,
      CategorySelect,
      RadioGroup,
      RadioItem,
      StatusInfo,
    },
  };
</script>
<style lang="scss" scoped>
  @import '../style';
</style>
